import { useState, useEffect } from "react";
import { Copy, RotateCw, X } from "lucide-react";
import { supabase, BRAND, INK, MUTED, FAINT, LINE, SURFACE, text, btn, btnSm, linkBtn, iconBtn, errorBanner, errorText, savedText, SCRIM, WHITE, BRAND_DARK, TEXT } from "./constants";
import { CloseButton, ConfirmBar } from "./shared";
import { getDefaultReferenceQuestions, fillQuestionTemplate } from "./referenceQuestionsDefault";
import { useIsMobile } from "./lib/responsive";
import { authFetch } from "./lib/ai";

// Móvil: los modales pasan a hoja a pantalla completa (blanca, cabecera
// propia con cerrar de 40 px, scroll propio, por encima de la barra inferior).
// Campos de 44 px y 16 px de letra (iOS amplía por debajo de 16).
const SHEET_OVERLAY_M = { background: WHITE, padding: 0, alignItems: "stretch", justifyContent: "stretch" };
const SHEET_PANEL_M = { borderRadius: 0, maxWidth: "none", maxHeight: "none", height: "100%", boxSizing: "border-box", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" };
const SHEET_HEAD_M = { padding: "8px 8px 8px 16px", gap: 12 };
const SHEET_BODY_M = { padding: 16, flex: 1, minHeight: 0 };
const SHEET_FOOT_M = { padding: "12px 16px" };
const SHEET_BTN_M = { flex: 1, height: 44, padding: "0 12px", minWidth: 0 };
const INPUT_M = { fontSize: 16, minHeight: 44 };

const genUUID = () => (crypto && crypto.randomUUID ? crypto.randomUUID() : "id-" + Math.random().toString(36).slice(2) + Date.now());

const ReferenceManager = ({ candidate, lang, isAdmin, onApproveReference, addButton = null }) => {
  const [tokens, setTokens] = useState([]); // all reference_intake_tokens for this candidate
  const [questions, setQuestions] = useState([]); // candidate-specific questions
  const [, setLoading] = useState(true);

  // Modals
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [reviewingToken, setReviewingToken] = useState(null); // token object for review
  const isMobile = useIsMobile();

  // Load tokens + questions
  const reload = async () => {
    if (!candidate?.id) return;
    const { data: t } = await supabase
      .from("reference_intake_tokens")
      .select("*")
      .eq("candidate_id", candidate.id)
      .order("created_at", { ascending: false });
    setTokens(t || []);

    // Load candidate questions or default
    const candQuestions = candidate.referenceQuestions;
    if (candQuestions && Array.isArray(candQuestions) && candQuestions.length > 0) {
      setQuestions(candQuestions);
    } else {
      setQuestions(getDefaultReferenceQuestions(lang));
    }
    setLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [candidate?.id, lang]);

  // Avisos y confirmaciones (23/10): antes ninguna escritura se comprobaba y
  // cancelar una petición o descartar una referencia se hacía con un clic
  const [rmMsg, setRmMsg] = useState(null); // { ok, text }
  const [askCancel, setAskCancel] = useState(null); // id del token
  const [askReject, setAskReject] = useState(false);
  const failMsg = () => setRmMsg({ ok: false, text: lang === "es" ? "No se pudo guardar el cambio. Inténtalo de nuevo." : "Couldn't save the change. Please try again." });
  const wrote = ({ data, error }) => !error && !!data && data.length > 0;

  const saveQuestions = async (newQs) => {
    const res = await supabase.from("candidates").update({ reference_questions: newQs }).eq("id", candidate.id).select("id");
    if (!wrote(res)) { failMsg(); return false; }
    setQuestions(newQs);
    return true;
  };

  const pendingTokens = tokens.filter(t => t.status === "pending");
  const submittedTokens = tokens.filter(t => t.status === "submitted");

  if (!isAdmin) return null; // Only admin uses this

  return (
    <>
      {/* Action buttons row */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", ...(isMobile ? { flexWrap: "wrap", width: "100%" } : null) }}>
        <button onClick={() => setShowQuestionsModal(true)}
          style={{ ...btn.ghost, padding: "9px 16px", display: "inline-flex", alignItems: "center", gap: 8, height: 36, boxSizing: "border-box", lineHeight: 1, ...(isMobile ? { height: 40, flex: "1 1 0", minWidth: 0, justifyContent: "center", padding: "0 10px", whiteSpace: "nowrap", order: 1 } : null) }}
          onMouseEnter={e => e.currentTarget.style.borderColor = BRAND}
          onMouseLeave={e => e.currentTarget.style.borderColor = LINE}>
          ✎ {lang === "es" ? "Editar preguntas" : "Edit questions"}
        </button>
        <button onClick={() => setShowSendModal(true)}
          style={{ ...btn.primary, border: "1.5px solid " + BRAND, padding: "9px 16px", display: "inline-flex", alignItems: "center", gap: 8, height: 36, boxSizing: "border-box", lineHeight: 1, ...(isMobile ? { height: 40, flex: "1 1 100%", minWidth: 0, justifyContent: "center", padding: "0 10px", whiteSpace: "nowrap", order: 0 } : null) }}
          onMouseEnter={e => { e.currentTarget.style.background = BRAND_DARK; e.currentTarget.style.borderColor = BRAND_DARK; }}
          onMouseLeave={e => { e.currentTarget.style.background = BRAND; e.currentTarget.style.borderColor = BRAND; }}>
          ✉ {lang === "es" ? "Pedir referencia" : "Send request"}
        </button>
        {addButton}
      </div>

      {/* Pending + submitted lists rendered below */}
      <PendingAndSubmittedLists
        pendingTokens={pendingTokens}
        submittedTokens={submittedTokens}
        lang={lang}
        candidateName={candidate.name}
        onReview={setReviewingToken}
        onCancel={(id) => setAskCancel(id)}
        onResend={async (tok) => {
          setRmMsg(null);
          const res = await authFetch("/api/send-email", {
            body: JSON.stringify({
              type: "reference_request",
              refereeName: tok.referee_name,
              refereeEmail: tok.referee_email,
              candidateName: candidate.name,
              token: tok.token,
              lang,
            })
          }).catch(() => null);
          setRmMsg(res && res.ok
            ? { ok: true, text: lang === "es" ? "Petición reenviada a " + tok.referee_email : "Request resent to " + tok.referee_email }
            : { ok: false, text: lang === "es" ? "No se pudo reenviar el correo." : "The email could not be resent." });
        }}
      />

      {/* Modal: Edit questions */}
      {showQuestionsModal && (
        <EditQuestionsModal
          initialQuestions={questions}
          lang={lang}
          candidateName={candidate.name}
          onClose={() => setShowQuestionsModal(false)}
          onSave={async (newQs) => { if (await saveQuestions(newQs)) setShowQuestionsModal(false); }}
        />
      )}

      {/* Modal: Send request */}
      {showSendModal && (
        <SendRequestModal
          candidate={candidate}
          questions={questions}
          lang={lang}
          onClose={() => setShowSendModal(false)}
          onSent={async () => { setShowSendModal(false); reload(); }}
        />
      )}

      {/* Modal: Review submitted */}
      {reviewingToken && (
        <ReviewSubmissionModal
          token={reviewingToken}
          candidateName={candidate.name}
          lang={lang}
          onClose={() => setReviewingToken(null)}
          onApprove={async (editedResponses) => {
            // Convert to reference and save
            const res = await supabase.from("reference_intake_tokens")
              .update({ responses: editedResponses, status: "approved", approved_at: new Date().toISOString() })
              .eq("id", reviewingToken.id).select("id");
            if (!wrote(res)) { failMsg(); return; }
            // Build reference object from responses. title / subtitle / items
            // son el formato que pinta RefAccordion y que lee el análisis de
            // referencias: sin ellos la tarjeta salía en blanco.
            const qs = reviewingToken.questions || [];
            const refIdx = (candidate.refsData || []).length + 1;
            const newRef = {
              title: refIdx + ". " + (reviewingToken.referee_name || reviewingToken.referee_email || "") + (reviewingToken.referee_role ? " — " + reviewingToken.referee_role : ""),
              subtitle: reviewingToken.referee_email || "",
              items: qs.map(q => [fillQuestionTemplate(q.text, candidate.name), (editedResponses || {})[q.id]]).filter(([, v]) => v && String(v).trim()),
              name: reviewingToken.referee_name || "",
              role: reviewingToken.referee_role || "",
              email: reviewingToken.referee_email || "",
              source: "form",
              tokenId: reviewingToken.id,
              questions: reviewingToken.questions,
              responses: editedResponses,
              date: new Date().toISOString().slice(0, 10),
            };
            if (onApproveReference) await onApproveReference(newRef);
            setReviewingToken(null);
            reload();
          }}
          onReject={() => setAskReject(true)}
        />
      )}
      {askReject && reviewingToken && (
        <ConfirmBar
          message={lang === "es"
            ? <>¿Descartar la referencia de <strong>{reviewingToken.referee_name || reviewingToken.referee_email}</strong>? No se añadirá a la ficha.</>
            : <>Discard the reference from <strong>{reviewingToken.referee_name || reviewingToken.referee_email}</strong>? It won't be added to the profile.</>}
          actionLabel={lang === "es" ? "Descartar" : "Discard"} cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
          onCancel={() => setAskReject(false)}
          onConfirm={async () => {
            setAskReject(false);
            const res = await supabase.from("reference_intake_tokens").update({ status: "rejected" }).eq("id", reviewingToken.id).select("id");
            if (!wrote(res)) { failMsg(); return; }
            setReviewingToken(null);
            reload();
          }} />
      )}
      {askCancel && (
        <ConfirmBar
          message={lang === "es" ? "¿Cancelar esta petición? El enlace que tiene el referente dejará de funcionar." : "Cancel this request? The link the referee has will stop working."}
          actionLabel={lang === "es" ? "Cancelar petición" : "Cancel request"} cancelLabel={lang === "es" ? "Volver" : "Back"}
          onCancel={() => setAskCancel(null)}
          onConfirm={async () => {
            const id = askCancel;
            setAskCancel(null);
            const res = await supabase.from("reference_intake_tokens").delete().eq("id", id).select("id");
            if (!wrote(res)) { failMsg(); return; }
            reload();
          }} />
      )}
      {rmMsg && <div style={{ ...(rmMsg.ok ? savedText : errorText), marginTop: 10 }}>{rmMsg.text}</div>}
    </>
  );
};

// ─── Sub-component: pending + submitted lists ────────────────────────

const PendingAndSubmittedLists = ({ pendingTokens, submittedTokens, lang, candidateName, onReview, onCancel, onResend }) => {
  const isMobile = useIsMobile();
  if (pendingTokens.length === 0 && submittedTokens.length === 0) return null;
  return (
    <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8, ...(isMobile ? { width: "100%", marginTop: 8 } : null) }}>
      {submittedTokens.map(tok => (
        <div key={tok.id} style={{ background: SURFACE, border: "1.5px solid " + LINE, borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, ...(isMobile ? { padding: "10px 12px", flexWrap: "wrap" } : null) }}>
          <div style={isMobile ? { minWidth: 0, flex: "1 1 160px", overflowWrap: "anywhere" } : undefined}>
            <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>
              {lang === "es" ? "Esperando revisión" : "Awaiting review"}: {tok.referee_name || tok.referee_email}
            </div>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
              {lang === "es" ? "Enviado" : "Submitted"} {tok.submitted_at ? new Date(tok.submitted_at).toLocaleDateString(lang === "es" ? "es-ES" : "en-GB") : ""}
            </div>
          </div>
          <button onClick={() => onReview(tok)}
            style={{ ...btn.primary, ...btnSm, ...(isMobile ? { height: 40, padding: "0 16px", fontSize: 13 } : null) }}>
            {lang === "es" ? "Revisar" : "Review"}
          </button>
        </div>
      ))}
      {pendingTokens.map(tok => (
        <div key={tok.id} style={{ background: SURFACE, border: "1.5px solid " + LINE, borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, ...(isMobile ? { padding: "10px 12px", flexWrap: "wrap" } : null) }}>
          <div style={isMobile ? { minWidth: 0, flex: "1 1 160px", overflowWrap: "anywhere" } : undefined}>
            <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>
              {lang === "es" ? "Esperando respuesta" : "Awaiting response"}: {tok.referee_name || tok.referee_email}
            </div>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
              {lang === "es" ? "Enviado" : "Sent"} {tok.created_at ? new Date(tok.created_at).toLocaleDateString(lang === "es" ? "es-ES" : "en-GB") : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={async () => { await navigator.clipboard.writeText(`https://www.rutment.com?ref=${tok.token}&lang=${lang === "es" ? "es" : "en"}`); }} title={lang === "es" ? "Copiar link" : "Copy link"}
              style={{ ...btn.ghost, ...btnSm, padding: "6px 10px", display: "inline-flex", alignItems: "center", justifyContent: "center", ...(isMobile ? { width: 40, height: 40, padding: 0, fontSize: 13 } : null) }}>
              <Copy size={14} strokeWidth={1.75} />
            </button>
            <button onClick={() => onResend(tok)} title={lang === "es" ? "Reenviar email" : "Resend email"}
              style={{ ...btn.ghost, ...btnSm, padding: "6px 10px", display: "inline-flex", alignItems: "center", justifyContent: "center", ...(isMobile ? { width: 40, height: 40, padding: 0, fontSize: 13 } : null) }}>
              <RotateCw size={14} strokeWidth={1.75} />
            </button>
            <button onClick={() => onCancel(tok.id)} title={lang === "es" ? "Cancelar" : "Cancel"}
              style={{ ...btn.danger, ...btnSm, padding: "6px 10px", display: "inline-flex", alignItems: "center", justifyContent: "center", ...(isMobile ? { width: 40, height: 40, padding: 0, fontSize: 13 } : null) }}>
              <X size={14} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Sub-component: Edit Questions Modal ─────────────────────────────

const EditQuestionsModal = ({ initialQuestions, lang, candidateName, onClose, onSave }) => {
  const [qs, setQs] = useState(initialQuestions);
  const isMobile = useIsMobile();

  const updateQ = (idx, field, value) => setQs(prev => prev.map((q, i) => i === idx ? { ...q, [field]: value } : q));
  const moveUp = idx => idx > 0 && setQs(prev => { const c = [...prev]; [c[idx-1], c[idx]] = [c[idx], c[idx-1]]; return c; });
  const moveDown = idx => idx < qs.length - 1 && setQs(prev => { const c = [...prev]; [c[idx+1], c[idx]] = [c[idx], c[idx+1]]; return c; });
  const removeQ = idx => setQs(prev => prev.filter((_, i) => i !== idx));
  const addQ = () => setQs(prev => [...prev, { id: genUUID(), text: "", type: "textarea", required: false }]);
  const reset = () => setQs(getDefaultReferenceQuestions(lang));

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: SCRIM, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, ...(isMobile ? SHEET_OVERLAY_M : null) }}>
      <div onClick={e => e.stopPropagation()} style={{ background: WHITE, borderRadius: 14, width: "100%", maxWidth: 720, maxHeight: "90vh", display: "flex", flexDirection: "column", ...(isMobile ? SHEET_PANEL_M : null) }}>
        <div style={{ padding: "20px 24px", borderBottom: `1.5px solid ${LINE}`, display: "flex", alignItems: "center", justifyContent: "space-between", ...(isMobile ? SHEET_HEAD_M : null) }}>
          <div>
            <div style={text.cardTitle}>{lang === "es" ? "Preguntas de referencia" : "Reference questions"}</div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{lang === "es" ? "Usa {candidateName} como variable para el nombre" : "Use {candidateName} as a variable for the name"}</div>
          </div>
          <CloseButton onClick={onClose} label={lang === "es" ? "Cerrar" : "Close"} />
        </div>
        <div style={{ ...(isMobile ? SHEET_BODY_M : { padding: 24 }), overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
          {qs.map((q, idx) => (
            <div key={q.id} style={{ background: SURFACE, border: `1.5px solid ${LINE}`, borderRadius: 10, padding: 14 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: MUTED, paddingTop: 8, minWidth: 20 }}>{idx + 1}.</div>
                <textarea
                  value={q.text}
                  onChange={e => updateQ(idx, "text", e.target.value)}
                  rows={2}
                  placeholder={lang === "es" ? "Texto de la pregunta" : "Question text"}
                  style={{ flex: 1, padding: "8px 12px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box", ...(isMobile ? { ...INPUT_M, minWidth: 0 } : null) }}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, paddingLeft: isMobile ? 0 : 30 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: TEXT, cursor: "pointer", ...(isMobile ? { minHeight: 40, fontSize: 13 } : null) }}>
                  <input type="checkbox" checked={q.required} onChange={e => updateQ(idx, "required", e.target.checked)} />
                  {lang === "es" ? "Obligatoria" : "Required"}
                </label>
                <div style={{ display: "flex", gap: 4 }}>
                  <button onClick={() => moveUp(idx)} disabled={idx === 0} style={{ background: "transparent", border: "none", color: idx === 0 ? FAINT : TEXT, cursor: idx === 0 ? "default" : "pointer", padding: "4px 8px", fontSize: 13, fontFamily: "inherit", ...(isMobile ? { width: 40, height: 40, padding: 0 } : null) }}>↑</button>
                  <button onClick={() => moveDown(idx)} disabled={idx === qs.length - 1} style={{ background: "transparent", border: "none", color: idx === qs.length - 1 ? FAINT : TEXT, cursor: idx === qs.length - 1 ? "default" : "pointer", padding: "4px 8px", fontSize: 13, fontFamily: "inherit", ...(isMobile ? { width: 40, height: 40, padding: 0 } : null) }}>↓</button>
                  <button onClick={() => removeQ(idx)} aria-label={lang === "es" ? "Quitar pregunta" : "Remove question"} title={lang === "es" ? "Quitar pregunta" : "Remove question"} style={{ ...iconBtn, color: FAINT, ...(isMobile ? { width: 40, height: 40 } : null) }}><X size={16} strokeWidth={1.75} /></button>
                </div>
              </div>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, ...(isMobile ? { gap: 8, alignItems: "center" } : null) }}>
            <button onClick={addQ} style={{ background: WHITE, color: BRAND, border: "1.5px dashed " + BRAND, borderRadius: 8, padding: "8px 16px", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit", ...(isMobile ? { height: 44, fontSize: 13 } : null) }}>+ {lang === "es" ? "Añadir pregunta" : "Add question"}</button>
            <button onClick={reset} style={{ ...linkBtn, color: MUTED, fontWeight: 600, ...(isMobile ? { minHeight: 40 } : null) }}>{lang === "es" ? "Restaurar por defecto" : "Reset to default"}</button>
          </div>
        </div>
        <div style={{ padding: "16px 24px", borderTop: `1.5px solid ${LINE}`, ...(isMobile ? SHEET_FOOT_M : null), display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ ...btn.ghost, ...(isMobile ? SHEET_BTN_M : null) }}>{lang === "es" ? "Cancelar" : "Cancel"}</button>
          <button onClick={() => onSave(qs)} style={{ ...btn.primary, ...(isMobile ? SHEET_BTN_M : null) }}>{lang === "es" ? "Guardar" : "Save"}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Sub-component: Send Request Modal ───────────────────────────────

const SendRequestModal = ({ candidate, questions, lang, onClose, onSent }) => {
  const [refereeName, setRefereeName] = useState("");
  const [refereeEmail, setRefereeEmail] = useState("");
  const [refereeRole, setRefereeRole] = useState("");
  const [subject, setSubject] = useState(
    lang === "es" ? `Referencia profesional para ${candidate.name}` : `Professional reference for ${candidate.name}`
  );
  const [body, setBody] = useState(
    lang === "es"
      ? `Hola,\n\nFuiste indicado/a como persona de referencia para ${candidate.name}, candidato/a para una posición que estamos cubriendo.\n\n¿Podrías dedicar unos minutos a completar el siguiente cuestionario? Tus respuestas son confidenciales y nos ayudarán a evaluar el encaje.\n\nGracias por tu tiempo.\nEquipo Rutment`
      : `Hi,\n\nYou were named as a professional reference for ${candidate.name}, a candidate for a position we're working on.\n\nCould you take a few minutes to complete the following questionnaire? Your answers are confidential and will help us assess the fit.\n\nThanks for your time.\nThe Rutment team`
  );
  const [creating, setCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState("");
  const [error, setError] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const isMobile = useIsMobile();

  // Create token first thing so the link can be generated for either action
  const ensureToken = async () => {
    if (createdLink) return createdLink;
    setCreating(true);
    setError("");
    if (!refereeName.trim() || !refereeEmail.trim() || !refereeEmail.includes("@")) {
      setError(lang === "es" ? "Nombre y email del referente son obligatorios" : "Referee name and valid email are required");
      setCreating(false);
      return null;
    }
    const { data, error: insErr } = await supabase
      .from("reference_intake_tokens")
      .insert({
        candidate_id: candidate.id,
        referee_name: refereeName.trim(),
        referee_email: refereeEmail.trim(),
        referee_role: refereeRole.trim(),
        questions,
        status: "pending",
      })
      .select("token")
      .single();
    setCreating(false);
    if (insErr || !data) { setError(lang === "es" ? "No se pudo crear el link" : "Could not create link"); return null; }
    // El enlace lleva el idioma de la petición: el formulario abre en él
    const link = `https://www.rutment.com?ref=${data.token}&lang=${lang === "es" ? "es" : "en"}`;
    setCreatedLink(link);
    return link;
  };

  const handleCopyLink = async () => {
    const link = await ensureToken();
    if (!link) return;
    await navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2500);
  };

  const handleSendEmail = async () => {
    const link = await ensureToken();
    if (!link) return;
    const token = link.split("ref=")[1].split("&")[0];
    const res = await authFetch("/api/send-email", {
      body: JSON.stringify({
        type: "reference_request",
        refereeName,
        refereeEmail,
        candidateName: candidate.name,
        subject,
        body,
        token,
        lang,
      })
    });
    if (res.ok) {
      setEmailSent(true);
      setTimeout(() => { onSent(); }, 1200);
    } else {
      setError(lang === "es" ? "Error al enviar el email" : "Email send failed");
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: SCRIM, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, ...(isMobile ? SHEET_OVERLAY_M : null) }}>
      <div onClick={e => e.stopPropagation()} style={{ background: WHITE, borderRadius: 14, width: "100%", maxWidth: 640, maxHeight: "90vh", display: "flex", flexDirection: "column", ...(isMobile ? SHEET_PANEL_M : null) }}>
        <div style={{ padding: "20px 24px", borderBottom: `1.5px solid ${LINE}`, display: "flex", alignItems: "center", justifyContent: "space-between", ...(isMobile ? SHEET_HEAD_M : null) }}>
          <div style={text.cardTitle}>{lang === "es" ? "Pedir referencia" : "Send reference request"}</div>
          <CloseButton onClick={onClose} label={lang === "es" ? "Cerrar" : "Close"} />
        </div>
        <div style={{ ...(isMobile ? SHEET_BODY_M : { padding: 24 }), overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ ...text.label, display: "block", marginBottom: 4 }}>{lang === "es" ? "Nombre del referente" : "Referee name"} *</label>
              <input value={refereeName} onChange={e => setRefereeName(e.target.value)} placeholder="Jane Doe" style={{ width: "100%", padding: "9px 12px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box", ...(isMobile ? INPUT_M : null) }} />
            </div>
            <div>
              <label style={{ ...text.label, display: "block", marginBottom: 4 }}>{lang === "es" ? "Email" : "Email"} *</label>
              <input value={refereeEmail} onChange={e => setRefereeEmail(e.target.value)} placeholder="jane@example.com" style={{ width: "100%", padding: "9px 12px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box", ...(isMobile ? INPUT_M : null) }} />
            </div>
          </div>
          <div>
            <label style={{ ...text.label, display: "block", marginBottom: 4 }}>{lang === "es" ? "Relación con el candidato" : "Relation to candidate"}</label>
            <input value={refereeRole} onChange={e => setRefereeRole(e.target.value)} placeholder={lang === "es" ? "Antiguo manager, peer..." : "Former manager, peer..."} style={{ width: "100%", padding: "9px 12px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box", ...(isMobile ? INPUT_M : null) }} />
          </div>
          <div>
            <label style={{ ...text.label, display: "block", marginBottom: 4 }}>{lang === "es" ? "Asunto del email" : "Email subject"}</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} style={{ width: "100%", padding: "9px 12px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box", ...(isMobile ? INPUT_M : null) }} />
          </div>
          <div>
            <label style={{ ...text.label, display: "block", marginBottom: 4 }}>{lang === "es" ? "Cuerpo del email" : "Email body"}</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={8} style={{ width: "100%", padding: "9px 12px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box", resize: "vertical", lineHeight: 1.5, ...(isMobile ? INPUT_M : null) }} />
          </div>
          {error && <div style={errorBanner}>{error}</div>}
        </div>
        <div style={{ padding: "16px 24px", borderTop: `1.5px solid ${LINE}`, ...(isMobile ? SHEET_FOOT_M : null), display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={handleCopyLink} disabled={creating}
            style={{ ...btn.ghost, cursor: creating ? "not-allowed" : "pointer", ...(isMobile ? SHEET_BTN_M : null) }}>
            {linkCopied ? (lang === "es" ? "✓ Copiado" : "✓ Copied") : (lang === "es" ? "Copiar link" : "Copy link")}
          </button>
          <button onClick={handleSendEmail} disabled={creating || emailSent}
            style={{ ...(emailSent ? btn.disabled : btn.primary), cursor: creating || emailSent ? "not-allowed" : "pointer", ...(isMobile ? SHEET_BTN_M : null) }}>
            {emailSent ? (lang === "es" ? "✓ Enviado" : "✓ Sent") : (lang === "es" ? "Enviar email" : "Send email")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Sub-component: Review Submission Modal ──────────────────────────

const ReviewSubmissionModal = ({ token, candidateName, lang, onClose, onApprove, onReject }) => {
  const [responses, setResponses] = useState(token.responses || {});
  const isMobile = useIsMobile();
  const questions = token.questions || [];

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: SCRIM, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, ...(isMobile ? SHEET_OVERLAY_M : null) }}>
      <div onClick={e => e.stopPropagation()} style={{ background: WHITE, borderRadius: 14, width: "100%", maxWidth: 760, maxHeight: "90vh", display: "flex", flexDirection: "column", ...(isMobile ? SHEET_PANEL_M : null) }}>
        <div style={{ padding: "20px 24px", borderBottom: `1.5px solid ${LINE}`, display: "flex", alignItems: "center", justifyContent: "space-between", ...(isMobile ? SHEET_HEAD_M : null) }}>
          <div>
            <div style={text.cardTitle}>{lang === "es" ? "Revisar referencia" : "Review reference"}</div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{token.referee_name} ({token.referee_email}) — {lang === "es" ? "Edita el texto si hace falta antes de aprobar" : "Edit text if needed before approving"}</div>
          </div>
          <CloseButton onClick={onClose} label={lang === "es" ? "Cerrar" : "Close"} />
        </div>
        <div style={{ ...(isMobile ? SHEET_BODY_M : { padding: 24 }), overflowY: "auto", display: "flex", flexDirection: "column", gap: 18 }}>
          {questions.map((q, idx) => (
            <div key={q.id}>
              <div style={{ fontSize: 13, fontWeight: 700, color: INK, marginBottom: 6 }}>
                {idx + 1}. {fillQuestionTemplate(q.text, candidateName)}
              </div>
              <textarea
                value={responses[q.id] || ""}
                onChange={e => setResponses(prev => ({ ...prev, [q.id]: e.target.value }))}
                rows={3}
                style={{ width: "100%", padding: "10px 12px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box", resize: "vertical", lineHeight: 1.5, ...(isMobile ? INPUT_M : null) }}
              />
            </div>
          ))}
        </div>
        <div style={{ padding: "16px 24px", borderTop: `1.5px solid ${LINE}`, ...(isMobile ? SHEET_FOOT_M : null), display: "flex", justifyContent: "space-between", gap: 8, ...(isMobile ? { flexWrap: "wrap" } : null) }}>
          <button onClick={onReject} style={{ ...btn.danger, ...(isMobile ? SHEET_BTN_M : null) }}>{lang === "es" ? "Descartar" : "Reject"}</button>
          <div style={{ display: "flex", gap: 8, ...(isMobile ? { width: "100%", order: -1 } : null) }}>
            <button onClick={onClose} style={{ ...btn.ghost, ...(isMobile ? SHEET_BTN_M : null) }}>{lang === "es" ? "Cerrar" : "Close"}</button>
            <button onClick={() => onApprove(responses)} style={{ ...btn.primary, ...(isMobile ? SHEET_BTN_M : null) }}>{lang === "es" ? "Aprobar y añadir" : "Approve and add"}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferenceManager;
